import React, { useMemo } from "react";
import { AgGridReact } from "ag-grid-react";
import { ColDef } from "ag-grid-community";
import { Modal } from "../../Modal/Modal";

import "./AwardDetailsModal.scss";

export interface IAwardDetails {
  registered_name: string;
  breed: string;
  arm_number: string;
  level?: string;
  advancedBScore?: number;
  excellentBScore?: number;
  masterScore?: number;
  total: number;
  totalTime: number;
  advancedBTime?: number;
  excellentBTime?: number;
  masterTime?: number;
}

interface IProps {
  isOpen: boolean;
  onClose: () => void;
  awardTitle: string;
  showName: string;
  showType: string;
  showDate: string;
  details: IAwardDetails[];
  awardType: 'highCombined' | 'highTriple';
}

const AwardDetailsModal: React.FC<IProps> = ({ isOpen, onClose, awardTitle, showName, showType, showDate, details, awardType }) => {
  const formatTime = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    const ms = Math.round((seconds % 1) * 100);
    return `${minutes}:${secs.toString().padStart(2, "0")}.${ms.toString().padStart(2, "0")}`;
  };

  const columnDefs: ColDef[] = useMemo(() => {
    // Determine if this is Obedience High Combined (has both Open B and Utility B)
    const isObedienceCombined = awardTitle.includes("Open B") && awardTitle.includes("Utility B");
    // Determine if this is a single-score obedience award (High in Trial, High Score in Regular/Preferred Classes)
    const isSingleScoreObedience = awardTitle.includes("High in Trial") || 
                                   awardTitle.includes("High Score in Regular Classes") || 
                                   awardTitle.includes("High Score in Preferred Classes");
    
    const cols: ColDef[] = [
      {
        headerName: "Arm #",
        field: "arm_number",
        flex: 0.3,
        minWidth: 115,
        width: 115,
        pinned: "left",
        cellClass: "armband-cell"
      },
      {
        headerName: "Dog Name",
        field: "registered_name",
        flex: 1.6,
        minWidth: 130,
        pinned: "left"
      },
      {
        headerName: "Breed",
        field: "breed",
        flex: 1.1,
        minWidth: 100
      }
    ];

    // For single-score Obedience awards, add Class column before Score
    if (isSingleScoreObedience) {
      cols.push({
        headerName: "Class",
        field: "level",
        flex: 1.1,
        minWidth: 120
      });
      cols.push({
        headerName: "Score",
        field: "advancedBScore",
        flex: 0.7,
        minWidth: 70,
        type: "numericColumn",
        cellClass: "total-cell",
        valueFormatter: (params) => params.value?.toFixed(1) || '-'
      });
    } else {
      // For Combined awards, show the two component scores
      cols.push(
        {
          headerName: isObedienceCombined ? "Open B" : "Advanced B",
          field: "advancedBScore",
          flex: 0.7,
          minWidth: 75,
          type: "numericColumn",
          valueFormatter: (params) => params.value?.toFixed(1) || '-'
        },
        {
          headerName: isObedienceCombined ? "Utility B" : "Excellent B",
          field: "excellentBScore",
          flex: 0.7,
          minWidth: 75,
          type: "numericColumn",
          valueFormatter: (params) => params.value?.toFixed(1) || '-'
        }
      );

      // Add Master column only for High Triple
      if (awardType === 'highTriple') {
        cols.push({
          headerName: "Master",
          field: "masterScore",
          flex: 0.7,
          minWidth: 75,
          type: "numericColumn",
          valueFormatter: (params) => params.value?.toFixed(1) || '-'
        });
      }

      // Add Total Time column only for Rally (not Obedience)
      if (!isObedienceCombined) {
        cols.push({
          headerName: "Total Time",
          field: "totalTime",
          flex: 0.7,
          minWidth: 80,
          valueFormatter: (params) => params.value ? formatTime(params.value) : '-'
        });
      }

      // Add Total Score for combined awards
      cols.push({
        headerName: "Total Score",
        field: "total",
        flex: 0.7,
        minWidth: 75,
        type: "numericColumn",
        cellClass: "total-cell",
        valueFormatter: (params) => params.value?.toFixed(1) || '-'
      });
    }

    return cols;
  }, [awardType, awardTitle]);

  const defaultColDef = useMemo(() => ({
    sortable: true,
    resizable: true,
    filter: true
  }), []);

  const getRowClass = (params: any) => {
    // Highlight the winner (first row after sorting)
    return params.node.rowIndex === 0 ? 'winner-row' : '';
  };

  const isObedienceCombined = awardTitle.includes("Open B") && awardTitle.includes("Utility B");
  const isSingleScoreObedience = awardTitle.includes("High in Trial") ||
    awardTitle.includes("High Score in Regular Classes") ||
    awardTitle.includes("High Score in Preferred Classes");

  const sortedDetails = [...details].sort((a, b) => b.total - a.total);

  return (
    <Modal
      className="AwardDetailsModal"
      isShown={isOpen}
      hide={onClose}
      modalContent={
        <div className="modalContent">
          <div className="modalHeader">
            <h2>{awardTitle}</h2>
            <h3>{showDate}</h3>
            <h3>{showType} {showName}</h3>
          </div>
          <div className="modalBody">
            {/* Screen: AG Grid */}
            <div className="ag-theme-alpine screen-only" style={{ height: '500px', width: '100%' }}>
              <AgGridReact
                rowData={details}
                columnDefs={columnDefs}
                defaultColDef={defaultColDef}
                getRowClass={getRowClass}
                suppressMovableColumns={true}
                animateRows={true}
              />
            </div>

            {/* Print: plain HTML table */}
            <table className="print-table">
              <thead>
                <tr>
                  <th className="col-arm">Arm #</th>
                  <th className="col-name">Dog Name</th>
                  <th className="col-breed">Breed</th>
                  {isSingleScoreObedience && <th className="col-class">Class</th>}
                  {isSingleScoreObedience
                    ? <th className="col-num">Score</th>
                    : <>
                        <th className="col-num">{isObedienceCombined ? "Open B" : "Advanced B"}</th>
                        <th className="col-num">{isObedienceCombined ? "Utility B" : "Excellent B"}</th>
                        {awardType === 'highTriple' && <th className="col-num">Master</th>}
                        {!isObedienceCombined && <th className="col-num">Total Time</th>}
                        <th className="col-num">Total Score</th>
                      </>
                  }
                </tr>
              </thead>
              <tbody>
                {sortedDetails.map((row, i) => (
                  <tr key={i} className={i === 0 ? 'winner' : ''}>
                    <td className="col-arm">{row.arm_number}</td>
                    <td className="col-name">{row.registered_name}</td>
                    <td className="col-breed">{row.breed}</td>
                    {isSingleScoreObedience && <td className="col-class">{row.level}</td>}
                    {isSingleScoreObedience
                      ? <td className="col-num">{row.advancedBScore?.toFixed(1) ?? '-'}</td>
                      : <>
                          <td className="col-num">{row.advancedBScore?.toFixed(1) ?? '-'}</td>
                          <td className="col-num">{row.excellentBScore?.toFixed(1) ?? '-'}</td>
                          {awardType === 'highTriple' && <td className="col-num">{row.masterScore?.toFixed(1) ?? '-'}</td>}
                          {!isObedienceCombined && <td className="col-num">{row.totalTime ? formatTime(row.totalTime) : '-'}</td>}
                          <td className="col-num total">{row.total?.toFixed(1) ?? '-'}</td>
                        </>
                    }
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      }
    />
  );
};

export default AwardDetailsModal;
